import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Grid,
  FormHelperText,
  MenuItem,
  CircularProgress,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import { Match } from '../types';
import CustomSelect from 'src/components/forms/theme-elements/CustomSelect';
import CustomFormLabel from 'src/components/forms/theme-elements/CustomFormLabel';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useGetTargettedTeamsToSeedMutation, useSeedMatchMutation } from 'src/services/tournaments';
import { useParams } from 'react-router';
import useToast from 'src/hooks/useToast';

const SeedMatchForm = ({
  open,
  setOpen,
  match,
  loadMatches,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  match: Match;
  loadMatches: () => Promise<void>;
}) => {
  const { id } = useParams();
  const { toastPromise } = useToast();

  const [getTargettedTeams, { isLoading, isError }] = useGetTargettedTeamsToSeedMutation();
  const [seedMatch] = useSeedMatchMutation();

  const [targettedTeams, setTargettedTeams] = useState<any[]>([]);

  const formik = useFormik({
    initialValues: {
      current_team_id: undefined,
      target_team_id: undefined,
    },
    validationSchema: Yup.object({
      current_team_id: Yup.number().required(t('TournamentScheduleTab.currentTeamRequired')),
      target_team_id: Yup.number().required(t('TournamentScheduleTab.targetTeamRequired')),
    }),
    onSubmit: (values) => {
      toastPromise({
        request: () =>
          seedMatch({
            tournamentId: Number(id),
            matchId: Number(match?.id),
            body: values,
          }),
        onSuccess: (result) => {
          if ('error' in result) {
            throw result.error;
          }

          setOpen(false);
          loadMatches();

          return t('TournamentScheduleTab.matchSeededSuccessfully');
        },
        onError: (err) => {
          if (err.data && err.data.errors) {
            const errorMessages = Object.values(err.data.errors).flat().join('. ');

            return errorMessages;
          }
          if (err?.data?.message) {
            return err?.data?.message;
          }

          return t('TournamentScheduleTab.matchSeedingFailed');
        },
      });
    },
  });

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  useEffect(() => {
    if (open && formik.values.current_team_id) {
      getTargettedTeams({
        tournamentId: Number(id),
        matchId: Number(match.id),
      }).then((res) => {
        if ('error' in res) {
          console.log(res.error);

          return;
        }

        const teams = res.data?.data?.teams;

        if (teams && Array.isArray(teams)) {
          setTargettedTeams(teams);
        }
      });
    }
  }, [formik.values.current_team_id]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>{t('TournamentScheduleTab.seedMatch')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('TournamentScheduleTab.seedMatchForm')}</DialogContentText>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CustomFormLabel htmlFor="current_team_id">
                  {t('TournamentScheduleTab.currentTeam')}
                </CustomFormLabel>
                <CustomSelect
                  id="current_team_id"
                  fullWidth
                  {...formik.getFieldProps('current_team_id')}
                  error={formik.touched.current_team_id && Boolean(formik.errors.current_team_id)}
                  helperText={formik.touched.current_team_id && formik.errors.current_team_id}
                >
                  {match?.participants?.map((participant) => (
                    <MenuItem key={participant.id} value={participant.id}>
                      {participant.name}
                    </MenuItem>
                  ))}
                </CustomSelect>
                {formik.touched.current_team_id && formik.errors.current_team_id && (
                  <FormHelperText error>{formik.errors.current_team_id}</FormHelperText>
                )}
              </Grid>
              <Grid item xs={isLoading ? 11 : 12}>
                <CustomFormLabel htmlFor="target_team_id">
                  {t('TournamentScheduleTab.targetTeam')}
                </CustomFormLabel>
                <CustomSelect
                  id="target_team_id"
                  fullWidth
                  {...formik.getFieldProps('target_team_id')}
                  error={formik.touched.target_team_id && Boolean(formik.errors.target_team_id)}
                  helperText={formik.touched.target_team_id && formik.errors.target_team_id}
                  loading={isLoading}
                  disabled={isLoading || targettedTeams.length === 0}
                >
                  {targettedTeams.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </CustomSelect>
                {isError ? (
                  <Typography variant="caption" color="error">
                    {t('TournamentScheduleTab.noTargetTeamsForThisTeam')}
                  </Typography>
                ) : (
                  formik.touched.target_team_id &&
                  formik.errors.target_team_id && (
                    <FormHelperText error>{formik.errors.target_team_id}</FormHelperText>
                  )
                )}
              </Grid>
              <Grid
                item
                xs={1}
                sx={{
                  display: isLoading ? 'flex' : 'none',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mt: 6,
                }}
              >
                <CircularProgress size={15} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit">{t('TournamentScheduleTab.seed')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SeedMatchForm;
